import styled from 'styled-components';
import Stats from 'components/Stats';
import {
  countStatisticsBySectionArray,
  COUNT_STATISTICS_DATA_TYPE,
} from 'StatisticsModule/constants';
import { conditionallyRenderElement } from 'utils/display';
import { Box, Grid, Divider } from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';
import {
  CountStatisticsValues,
  CountStatisticsDataType,
  FetchCountStatisticsResponseData,
} from 'StatisticsModule/types';
import { calculatePercentageDifferenceBetweenPreviousAndAfterValues } from 'utils';
import { ValueOf } from 'type-fest';
import { ActiveUserCountCard } from './../ActiveUserCountCard';

const StyledBox = styled(Box)`
  & > * {
    font-size: 13px;
    padding: 5px;
  }
`;

const getFilteredTotalCountsTooltip = (
  countsByDataType: CountStatisticsValues,
  dataType: CountStatisticsDataType,
) => {
  if (dataType === COUNT_STATISTICS_DATA_TYPE.TEAM_MEMBERSHIPS) {
    const {
      total_count,
      filtered_counts = {
        basic_member: 0,
        contractor_external: 0,
        contractor_internal: 0,
        guest: 0,
        archived: 0,
      },
    } = countsByDataType;
    const { basic_member, contractor_external, contractor_internal, guest, archived } =
      filtered_counts;

    return (
      <StyledBox>
        <div>Total Counts: {total_count}</div>
        <div>Basic Members: {basic_member}</div>
        <div>External Contractors: {contractor_external}</div>
        <div>Internal Contractors: {contractor_internal}</div>
        <div>Guests: {guest}</div>
        <div>Archived: {archived}</div>
      </StyledBox>
    );
  }

  const { total_count, filtered_count } = countsByDataType;

  return (
    <StyledBox>
      <div>Total Counts: {total_count || 0}</div>
      <div>Filtered Counts: {filtered_count || 0}</div>
    </StyledBox>
  );
};

const getFilteredTotalCounts = (
  countsByDataType: CountStatisticsValues,
  dataType: CountStatisticsDataType,
) => {
  if (dataType === COUNT_STATISTICS_DATA_TYPE.TEAM_MEMBERSHIPS) {
    const {
      filtered_counts = {
        basic_member: 0,
        contractor_external: 0,
        contractor_internal: 0,
        guest: 0,
        archived: 0,
      },
    } = countsByDataType;
    const { basic_member, contractor_external, contractor_internal, guest, archived } =
      filtered_counts;

    return basic_member + contractor_external + contractor_internal + guest + archived;
  }

  return countsByDataType.filtered_count || 0;
};

const getPercentageDifferenceProps = (
  countsStatisticsValuesByDataType: CountStatisticsValues,
  previousPeriodValuesByDataType: CountStatisticsValues,
  dataType: CountStatisticsDataType,
  searchDateRangeLabel: string,
) => {
  // const currentPeriodTotalCount = countsStatisticsValuesByDataType.total_count;
  // const previousPeriodTotalCount = previousPeriodValuesByDataType.total_count;

  const currentPeriodTotalFilteredCount = getFilteredTotalCounts(
    countsStatisticsValuesByDataType,
    dataType,
  );
  const previousPeriodTotalFilteredCount = getFilteredTotalCounts(
    previousPeriodValuesByDataType,
    dataType,
  );

  const percentageTooltip = (
    <StyledBox>
      <div>{`Percentage difference between ${searchDateRangeLabel} and the previous period of ${searchDateRangeLabel}.`}</div>
      <br />
      <div>
        {searchDateRangeLabel}&apos;s total filtered counts: {currentPeriodTotalFilteredCount}
      </div>
      <div>
        {`Period before ${searchDateRangeLabel}&apos;s total filtered counts`}:{' '}
        {previousPeriodTotalFilteredCount}
      </div>
    </StyledBox>
  );

  const { difference } = calculatePercentageDifferenceBetweenPreviousAndAfterValues(
    previousPeriodTotalFilteredCount,
    currentPeriodTotalFilteredCount,
  );

  const hasNotDecreased = difference >= 0;
  const formattedDifference = Math.round(difference * 100);

  const percentageText = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
      }}
    >
      {hasNotDecreased ? (
        <TrendingUp sx={{ fontSize: 16 }} />
      ) : (
        <TrendingDown sx={{ fontSize: 16 }} />
      )}
      <span>{hasNotDecreased ? `+${formattedDifference}` : formattedDifference}%</span>
    </Box>
  );

  return {
    percentageColor: hasNotDecreased ? 'green' : 'red',
    percentageText,
    percentageTooltip,
  };
};

const Dashboard = ({
  data,
  isLoading,
  dataTypeToIgnoreSet,
  searchDateRangeLabel,
  activeUserCount,
  activeUserSinceDate,
}: {
  data: FetchCountStatisticsResponseData;
  isLoading: boolean;
  searchDateRangeLabel: string;
  dataTypeToIgnoreSet?: Set<ValueOf<typeof COUNT_STATISTICS_DATA_TYPE>>;
  activeUserCount: number | string; // showing "loading" when fetching for number
  activeUserSinceDate?: string;
}) => {
  return (
    <Styler>
      {conditionallyRenderElement({
        isLoading,
        error: undefined,
        content: (
          <Box
            sx={{
              m: 10,
            }}
          >
            <ActiveUserCountCard
              activeUserSinceDate={activeUserSinceDate}
              activeUserCount={activeUserCount}
            />
            {countStatisticsBySectionArray.map((section) => {
              const { label: sectionLabel, value: dataTypes } = section;

              return (
                <Grid key={sectionLabel}>
                  <Box component={'h3'}>{sectionLabel}</Box>
                  <Divider />{' '}
                  <Box
                    sx={{
                      mt: 5,
                    }}
                  >
                    <Grid container spacing={4}>
                      {dataTypes.map((dataType) => {
                        const { count_statistics, previous_period_data } = data;

                        const countsStatisticsValuesByDataType = count_statistics?.[dataType.value];

                        const previousPeriodValuesByDataType =
                          previous_period_data?.[dataType.value];

                        const shouldIgnore = dataTypeToIgnoreSet?.has(dataType.value);
                        if (
                          (!countsStatisticsValuesByDataType && !previousPeriodValuesByDataType) ||
                          shouldIgnore
                        )
                          return null;

                        const { percentageColor, percentageText, percentageTooltip } =
                          getPercentageDifferenceProps(
                            countsStatisticsValuesByDataType || {},
                            previousPeriodValuesByDataType || {},
                            dataType.value,
                            searchDateRangeLabel,
                          );

                        return (
                          <Grid key={dataType.value} size="auto" style={{ width: 320 }}>
                            <Stats
                              title={
                                <Box>
                                  <Box
                                    sx={{
                                      fontSize: 10,
                                      color: (theme) => theme.palette.text.primary,
                                    }}
                                  >
                                    New
                                  </Box>
                                  <div>{dataType.label}</div>
                                </Box>
                              }
                              amount={getFilteredTotalCounts(
                                countsStatisticsValuesByDataType,
                                dataType.value,
                              )}
                              percentageTooltip={percentageTooltip}
                              percentageText={percentageText}
                              percentageTextPrefix={'Over previous period'}
                              percentagecolor={percentageColor}
                              amountTooltip={getFilteredTotalCountsTooltip(
                                countsStatisticsValuesByDataType,
                                dataType.value,
                              )}
                              chip={searchDateRangeLabel}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                </Grid>
              );
            })}
          </Box>
        ),
      })}
    </Styler>
  );
};

export default Dashboard;

const Styler = styled.div`
  margin-top: 3rem;
  .menu-container {
    background-color: transparent;
    width: fit-content;
  }
`;
